import { useState } from 'react';
import { cn } from '@/shared/utils/cn';
import type { PdpDesktopProductDetailsSection } from './PdpDesktopProductDetails';

interface PdpProductDetailsAccordionSectionsProps {
  sections: PdpDesktopProductDetailsSection[];
  className?: string;
  expandByDefault?: boolean;
}

export function PdpProductDetailsAccordionSections({
  sections,
  className,
  expandByDefault = true,
}: PdpProductDetailsAccordionSectionsProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () =>
      new Set(expandByDefault ? sections.map((section) => section.id) : [])
  );

  if (sections.length === 0) {
    return null;
  }

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div className={className}>
      {sections.map((section) => {
        const isExpanded = expandedSections.has(section.id);

        return (
          <div key={section.id}>
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              aria-expanded={isExpanded}
              className="flex w-full items-center justify-between border-t border-[#eee] bg-[#f9f9f9] px-4 py-2 text-left"
            >
              <span className="text-xs font-bold uppercase tracking-wide text-black">
                {section.title}
              </span>
              <span
                className={cn(
                  'text-xl font-light leading-none text-[#666]',
                  isExpanded && 'pb-0.5'
                )}
                aria-hidden
              >
                {isExpanded ? '−' : '+'}
              </span>
            </button>
            {isExpanded ? <div>{section.content}</div> : null}
          </div>
        );
      })}
    </div>
  );
}
